/**
 * A lesson from the cards: the public side of a shared reading (2026-10-25,
 * Claude outputs/community-lesson-design-2026-10-25/DESIGN.md). It replaces
 * the "public version" story. The server writes it under the privacy rule
 * (§7) and sends only the lesson to anyone outside the reading; nothing here
 * ever sees the reading itself.
 *
 * The lesson is the server's `public_version` (build/API.md §0): { short_uuid,
 * question, lesson, image_url ("" when the picture can't be used, rule 6),
 * cards_lead, cards: [{ role, name, reversed, image_url, line }], credit
 * ("Shared by Star Gazer"), credit_name, spoke_count, spoke, reader_name,
 * reader_subdomain, reader_avatar, starters, topic, topic_label, invite: {
 * line, say, starters, role, placeholder, button, note, href, own } }.
 * The feed's card (§1) is the same minus the lines and the invite.
 */

export const CARDS_HEADING = 'What the cards teach'
export const SPOKE_LABEL = 'This spoke to me'
export const SPOKE_SHORT = 'Spoke to me'
// The ending (lesson-ending DESIGN.md §3, 2026-10-27): the reading's Mystic,
// speaking about this lesson. The server's words win (lesson.invite); these
// are the design's, for an older server.
export const ENDING = {
  role: 'read these cards',
  line: (theme) => `I read these cards for a question about ${theme || 'something close to this'}.`,
  say: 'If something like it is on your mind, I’ll read yours. Tell me in your own words',
  placeholder: (mystic) => `Tell ${mystic} what’s on your mind…`,
  button: (mystic) => `Ask ${mystic}`,
  note: 'Nothing to sign up for first.',
  own: {
    role: 'read your cards',
    line: 'This lesson came from your reading with me.',
    say: 'Only its lesson is out here; your reading is still just yours. Is there more you want to ask about it?',
    placeholder: 'Ask me more about your reading…',
    button: 'Ask about this',
  },
}
// "A question about love": the topic as words in the eyebrow (share_topic keys).
export const TOPIC_WORDS = { love: 'love', money: 'money', work: 'work', health: 'health', family: 'friends & family', self: 'oneself', choices: 'a choice' }
const TOPIC_MORE = { love: 'love', money: 'money', work: 'work', health: 'health', family: 'friends & family', self: 'the self', choices: 'choices' }
export const SOMETHING_ELSE = 'Something else'
export const OWN_BACK = 'The lesson'

const isSeeker = (c) => !c || !String(c).trim() || String(c).trim().toLowerCase() === 'a seeker'

/** "Star Gazer" or "a seeker" (the server's credit_name, "" for a seeker). */
export function creditName(lesson) {
  if (!lesson) return 'a seeker'
  const n = lesson.credit_name !== undefined ? lesson.credit_name : String(lesson.credit || '').replace(/^shared by\s+/i, '')
  return isSeeker(n) ? 'a seeker' : String(n).trim()
}
export const creditLine = (lesson) => `Shared by ${creditName(lesson)}`

/**
 * Who a named credit points to, for their profile (community-simplify,
 * Chris 2026-10-25): the server's identifier for the seeker, never the
 * display text. '' for "Shared by a seeker" or when the server sends none.
 */
export function creditSeeker(lesson) {
  if (!lesson || creditName(lesson) === 'a seeker') return ''
  const id = lesson.credit_seeker   // build/API.md §4 (v1.1)
  return id && String(id).trim() ? String(id).trim() : ''
}
/**
 * The sharer's picture beside a named credit (Chris, 2026-10-26): the
 * server's `credit_avatar`, and only when the credit is their chosen name.
 * '' for "Shared by a seeker", whatever the payload carries.
 */
export function creditAvatar(lesson) {
  if (!creditSeeker(lesson)) return ''
  const url = lesson.credit_avatar
  return url && String(url).trim() ? String(url).trim() : ''
}
/** Open that seeker's profile in the global SeekerModal (over whatever is open). */
export function openCreditSeeker(lesson) {
  const nickName = creditSeeker(lesson)
  if (!nickName || typeof window === 'undefined') return false
  window.dispatchEvent(new CustomEvent('open-seeker-modal', { detail: { nickName } }))
  return true
}

/** The sharer's quiet tally (L4): only once either number is above zero. */
// The share state's tally: { spoke_to, drew_their_own, show }.
const tallyNums = (t) => ({ spoke: Number(t && t.spoke_to) || 0, drew: Number(t && t.drew_their_own) || 0 })
export function showTally(t) {
  const { spoke, drew } = tallyNums(t)
  return spoke > 0 || drew > 0
}
export function tallyLine(t) {
  const { spoke, drew } = tallyNums(t)
  const out = []
  if (spoke > 0) out.push({ n: spoke, words: `${spoke === 1 ? 'person' : 'people'} said it spoke to them` })
  if (drew > 0) out.push({ n: drew, words: 'drew their own cards from it' })
  return out
}

/** "WHAT CAME BEFORE · Ten of Pentacles" (the name in italic beside the role). */
export function cardLabel(c) {
  const name = (c && c.name) || ''
  return { role: (c && c.role) || '', name: c && c.reversed && name ? `${name}, reversed` : name }
}
export const lessonCards = (lesson) => ((lesson && lesson.cards) || []).filter((c) => c && (c.line || c.name))
export const hasPicture = (lesson) => !!(lesson && lesson.image_url && !lesson.cards_lead)
// Under a picture the four thumbnails follow the lesson; without one the cards lead on a plum band.
export const thumbsShown = (lesson) => hasPicture(lesson)

/** L5: the Mystic's page with where it came from (§6). */
export function drawHref(lesson) {
  if (lesson && lesson.invite && lesson.invite.href) return lesson.invite.href
  const sub = (lesson && lesson.reader_subdomain) || ''
  const short = (lesson && lesson.short_uuid) || ''
  const q = []
  if (sub) q.push(`reader=${encodeURIComponent(sub)}`)
  if (short) q.push(`from=${encodeURIComponent(short)}`)
  return `/?${q.join('&')}`
}

/** The opener's theme: "a question about X" → "X" (the server's theme_of). */
export function themeOf(question) {
  const q = String(question || '').trim().replace(/[.?!]+$/, '')
  const m = q.match(/^a question about\s+(.+)$/i)
  return m ? m[1] : ''
}

/**
 * The question leads (lesson-ending DESIGN.md §1): the eyebrow is "A question
 * about <topic>" ("A question" without one) and the headline is the question
 * without its fixed opening, first letter up.
 */
export function lessonEyebrow(lesson) {
  const words = TOPIC_WORDS[(lesson && lesson.topic) || '']
  return words ? `A question about ${words}` : 'A question'
}
export function questionHeadline(lesson) {
  const q = String((lesson && lesson.question) || '').trim()
  const t = themeOf(q) || q.replace(/[.?!]+$/, '')
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
}
/** "More about love": the heading over the related lessons. */
export function moreAbout(lesson) {
  const words = TOPIC_MORE[(lesson && lesson.topic) || '']
  return words ? `More about ${words}` : 'More lessons'
}

/**
 * L5's first turn, when the server sends none of its own: the theme, never
 * the sharer or the sharer's words; the starters and "Something else".
 */
export function openerLines(lesson) {
  const theme = themeOf(lesson && lesson.question)
  return {
    lines: [
      `You were reading a lesson${theme ? ` about ${theme}` : ''}. Is something like that on your mind?`,
      'Tell me in your own words, or start with one of these.',
    ],
    chips: [...((lesson && lesson.starters) || []).filter(Boolean).slice(0, 2), SOMETHING_ELSE],
  }
}

/**
 * The ending's words (lesson-ending DESIGN.md §3): the server's (`invite`,
 * since 2026-10-27 with `say`), else the design's. Anyone else: the Mystic
 * names the question, offers a reading in its own words, with the two
 * starters as replies. The sharer: about their own reading, no starters.
 */
export function inviteWords(lesson) {
  const inv = (lesson && lesson.invite) || {}
  const own = !!(inv.own || (lesson && lesson.is_own))
  const name = String((lesson && lesson.reader_name) || '').trim() || 'Mystic Aida'
  const href = drawHref(lesson)
  // An older server sends only line/button/note ("Carrying a question like this?").
  const srv = typeof inv.say === 'string' && inv.say.trim() ? inv : null
  if (own) {
    const o = ENDING.own
    return {
      own, name, href, starters: [], note: '',
      role: (srv && srv.role) || o.role, line: (srv && srv.line) || o.line, say: (srv && srv.say) || o.say,
      placeholder: (srv && srv.placeholder) || o.placeholder, button: (srv && srv.button) || o.button,
    }
  }
  const starters = ((srv && Array.isArray(srv.starters) ? srv.starters : (lesson && lesson.starters)) || [])
    .map((x) => String(x || '').trim()).filter(Boolean).slice(0, 2)
  return {
    own, name, role: (srv && srv.role) || ENDING.role,
    line: (srv && srv.line) || ENDING.line(themeOf(lesson && lesson.question)),
    say: (srv && srv.say) || ENDING.say,
    starters,
    placeholder: (srv && srv.placeholder) || ENDING.placeholder(name),
    button: (srv && srv.button) || ENDING.button(name),
    note: srv ? (srv.note || '') : ENDING.note,
    href,
  }
}

/** The reading-support answer: spoke / spoke_count (old names supported / support_count). */
export function spokeFrom(r) {
  if (!r || r.ok === false) return null
  const on = r.spoke !== undefined ? r.spoke : r.supported
  const n = r.spoke_count !== undefined ? r.spoke_count : r.support_count
  if (on === undefined) return null
  return { spoke: !!on, count: Number(n) || 0 }
}

// ─────────────────────────────────────────────────────────────────────────
// The lesson redo (2026-10-28, Claude outputs/lesson-redo-2026-10-27/
// DESIGN.md rev 9): the two-line story (H1), the four beats (H4), the
// picture's caption (F), the ending (E1), the sharer (E3).
// ─────────────────────────────────────────────────────────────────────────

/**
 * The lead-in (H1): "Someone asked:" and the asker's own question (the
 * server's `ask`), in quotes. An older lesson, until the backfill: "Someone
 * asked about" and its theme.
 */
export function leadIn(lesson) {
  // The sharer sees their own question as theirs (E5, rev 13): "You asked:".
  const who = lesson && (lesson.own || lesson.is_own) ? 'You' : 'Someone'
  const ask = String((lesson && lesson.ask) || '').trim()
  if (ask) return { key: `${who} asked:`, text: `“${ask}”` }
  const theme = String((lesson && lesson.theme) || '').trim() || themeOf(lesson && lesson.question)
  return theme ? { key: `${who} asked about`, text: theme } : { key: '', text: '' }
}

/** The four positions (H4): every four-card or four-placement lesson. */
export const POSITIONS = ['Before', 'Now', 'Ahead', 'What helps']

/**
 * The story in four beats (F, H4): each card (or astrology placement) with
 * its position, name, art and beat. `turn` is the server's 1-based TURN
 * (2 to 4; never Before). Older lessons keep their card lines, no turn.
 */
export function storyBeats(lesson) {
  const l = lesson || {}
  const places = Array.isArray(l.placements) && l.placements.length ? l.placements : null
  const items = places || lessonCards(l)
  const beats = Array.isArray(l.beats) ? l.beats : []
  const turnAt = Number(l.turn) >= 2 && Number(l.turn) <= 4 ? Number(l.turn) - 1 : -1
  const four = items.length === 4
  return items.map((c, i) => {
    const name = places ? String(c.name || '') : cardLabel(c).name
    return {
      pos: four ? POSITIONS[i] : (places ? (POSITIONS[i] || '') : (c.role || '')),
      name,
      image_url: places ? '' : (c.image_url || ''),
      reversed: !places && !!c.reversed,
      glyph: places ? String(c.glyph || '✦') : '',
      astro: !!places,
      line: String(beats[i] || (c && c.line) || '').trim(),
      turn: i === turnAt,
    }
  })
}

/** The picture's caption (F): the enlarged view and the picture button's name. */
export const captionOf = (lesson) => String((lesson && (lesson.caption || lesson.image_caption)) || '').trim()

/**
 * The caption under the plate (the arc, H7): only one written as a sentence
 * (`caption_kind: "turn"`). An old four-word title, or a payload without the
 * kind, stays in the enlarged view only.
 */
export const plateCaptionOf = (lesson) => (lesson && lesson.caption_kind === 'turn' ? captionOf(lesson) : '')

/** Astrology: the night sky stands in for the picture (F). */
export const isAstrology = (lesson) => !!(lesson && Array.isArray(lesson.placements) && lesson.placements.length)

// The ending's question for an older lesson (before ASK_YOU): the topic, turned to the reader.
const TURN_BY_TOPIC = {
  love: 'Wondering about love in your own life?', money: 'Wondering about your own finances?', work: 'Wondering about your own work?',
  health: 'Wondering about your own health?', family: 'Wondering about your own friends and family?', self: 'Wondering about your own path?',
  choices: 'Facing a choice of your own?', '': 'Something like this on your mind?',
}
/** E1's headline: the lesson's ASK_YOU, else its topic turned to the reader. */
export function askYou(lesson) {
  const q = String((lesson && lesson.ask_you) || '').trim()
  return q || TURN_BY_TOPIC[(lesson && lesson.topic) || ''] || TURN_BY_TOPIC['']
}
/** ABOUT (H6): the situation as a noun phrase, else the theme. */
export function aboutOf(lesson) {
  return String((lesson && lesson.about) || '').trim() || themeOf(lesson && lesson.question)
}

/** The quiet line over a card drawn from a lesson (2026-10-28): "From a lesson about {ABOUT}". */
export function lessonContextLine(from) {
  if (!from) return ''
  const about = String(from.about || '').trim()
  return about ? `From a lesson about ${about}` : 'From a lesson'
}

/**
 * The card this viewer drew from the lesson (API.md 0.2 `my_draw`, v1.6), or
 * null: one draw per lesson per seeker; the ending reopens it.
 */
export function myDrawOf(lesson) {
  const m = lesson && lesson.my_draw
  const card = String((m && m.card) || '').trim()
  if (!card) return null
  return {
    readingId: String(m.reading_id || ''), card, reversed: m.reversed === true,
    image: String(m.image || ''), line: String(m.line || '').trim(), href: String(m.href || ''),
  }
}

/** The draw's platform price (`lesson_draw`, J1): the server's, else 10. */
export const DRAW_PRICE = 10
export function drawPrice(lesson) {
  const l = lesson || {}
  const p = (l.ending && l.ending.price_carats) ?? l.draw_price
  return Number.isFinite(Number(p)) && p !== null && p !== '' ? Number(p) : DRAW_PRICE
}

/** The Mystic who read it (I): a square, a name, a page. */
export function mysticOf(lesson) {
  const l = lesson || {}
  const e = l.ending || {}
  return {
    name: String(e.reader_name || l.reader_name || '').trim() || 'Mystic Aida',
    sub: String(e.reader_subdomain || l.reader_subdomain || '').trim() || 'iam',
    avatar: String(e.reader_avatar || l.reader_avatar || '').trim(),
  }
}

/** Where the draw (or the talk) starts: the Mystic's fresh session from this lesson (E2, API.md 6.1). */
function lessonHref(lesson, how) {
  const { sub } = mysticOf(lesson)
  const short = (lesson && lesson.short_uuid) || ''
  return `/?reader=${encodeURIComponent(sub)}&from=${encodeURIComponent(short)}&ask=1&how=${how}`
}

/** E1: the ending's words, anyone but the sharer (the server's `ending` wins, API.md 0.2). */
export function endingWords(lesson) {
  const e = (lesson && lesson.ending) || {}
  const { name } = mysticOf(lesson)
  const price = drawPrice(lesson)
  const s = (k) => String(e[k] || '').trim()
  return {
    headline: s('headline') || askYou(lesson),
    line: s('line') || `Draw a card and ${name} will read it for where you are.`,
    button: s('button') || 'Draw a card',
    price,
    aria: s('aria') || `Draw a card with ${name}, ${price} carats`,
    talk: s('talk') || `Talk it over with ${name} instead`,
    drawHref: s('draw_href') || lessonHref(lesson, 'draw'),
    talkHref: s('talk_href') || lessonHref(lesson, 'talk'),
  }
}

/** E3: the sharer's numbers (invite.spoken / invite.drew), calm at zero. */
export function sharerLines(invite) {
  const n = Number(invite && invite.spoken) || 0
  const d = Number(invite && invite.drew) || 0
  const people = (k) => `${k} ${k === 1 ? 'person' : 'people'}`
  const out = [n > 0 ? `Your lesson has spoken to ${people(n)}.` : 'Your lesson is out in the Community now.']
  if (d > 0) out.push(`${people(d)} drew their own card after reading it.`)
  else if (n === 0) out.push('When it speaks to someone, you’ll see it here.')
  return out
}
export const SHARER_ASK = 'Ask more about your reading'

/** Open the Mystic's page in the app (ReaderPage listens), from a lesson's mark. */
export function openMystic(sub, via = 'lesson') {
  if (!sub || typeof window === 'undefined') return false
  window.dispatchEvent(new CustomEvent('open-reader-from-modal', { detail: { subdomain: sub, via } }))
  return true
}
